// 設備設定表單（SPEC DEV-06、#platform-ui-tiers、TIER-05）：由平台的能力投影產生（GET …/vms/devices/{d}/settings），不寫死機種。
//   - 只列設備有回報的 cluster；range / enum / presets 取能力模型，值取 reported；層（tier）由 app-api 求值，前端只信它
//   - view = settings：左側 section 清單、右側先畫常用，下方「進階（n）」折疊（展開狀態每人每 section 記在這個瀏覽器）；
//     analytics：kinio_analytics 的條目（獨立分頁）；technical：所有技術條目（只給 owner / admin，寫入前第 2 級確認）
//   - can_write = 伺服器照 write_authz 與角色算好的（owner-only 項目只有 owner 能改）；不能寫的顯示成唯讀
//   - 寫入走 shadow desired，之後輪詢到該 key 不在 desired（設備 ack、SPEC #shadow-sync）：pending → applied；
//     reported 跟寫入值不同 = 設備正規化或拒收 → adjusted、畫面以 reported 為準；25 秒沒收斂算 failed
//   - 碼流 / 畫質類設定先確認「會影響既有錄影與觀看」（標準整合 §7.2）；模型帶 confirm 的也先確認（第 2 級確認元件）
//   - segment = 一排 radio（正常 / 休眠 / 隱私）；presets = 選單；depends_on / depends_value 的上層沒開 → 停用並說明
//   - raw 自訂編輯器（SPEC #ls2505-334、W22）：照條目的 editor 欄挑（zones / privacy_masks → ZoneEditor、zone_grid → GridEditor、
//     schedule → ScheduleEditor），不看屬性名；列上顯示摘要（全畫面 / N 個區域；關 / N 個遮罩；全畫面 / 部分區域；全天 / 不啟用 / 自訂）。
//     沒有 editor 的 custom 與 command（button）整列不畫（網頁做不到的不出現，B-28；settingsTiers.webCannotOperate）
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  adopted,
  converge,
  encodePrivacyMasks,
  encodeZoneGrid,
  encodeZones,
  getShadow,
  getThing,
  parsePrivacyMasks,
  parseZones,
  privacyMasksConverged,
  scheduleConverged,
  scheduleSummary,
  setDesired,
  shadowAttr,
  zoneGridConverged,
  zoneGridSummary,
  zonesConverged,
  type ConvergeTarget,
  type Shadow,
  type ZoneGrid,
} from '@kinio/web-sdk';
import { ZoneEditor, type RectLayer, type ZoneValue } from './zed/ZoneEditor';
import { GridEditor } from './zed/GridEditor';
import { ScheduleEditor } from './zed/ScheduleEditor';
import { deviceApi, affectsStreams, confirmText, overwriteMode, type DeviceSettings, type SettingEditor, type SettingItem } from '../../lib/deviceApi';
import { enumText, itemLabel, kinioLabel, presetText } from '../../lib/kinioLabel';
import { QUALITY_TIER_KEY, qualityTierResolution, qualityTierValues, resolutionText } from '../../lib/recordingQuality';
import { capStringText, capStringValues } from '../../lib/capValues';
import { useApp } from '../../lib/AppContext';
import { useHomeTz } from '../../lib/useTz';
import { useConfirm } from '../../components/ConfirmDialog';
import { OverwriteLabel } from './OverwriteLabel';
import type { VmsDevice } from '../../lib/devices';
import {
  allItems,
  dependsOk,
  hideRows,
  layoutTiers,
  presetSelected,
  readAdvOpen,
  rowCount,
  segmentCurrent,
  segmentDesired,
  segmentOptions,
  displayUnits,
  settingText,
  userHelp,
  withVendorFirmware,
  SEGMENT_NONE,
  tierOf,
  writeAdvOpen,
  type Row,
  type RowGroup,
  type SettingsView,
} from './settingsTiers';

const OVERWRITE_KEY = 'kinio_storage.overwrite';

/** 字串型 select：模型沒有數字 enum（例 kinio_video.main_resolution "<寬>x<高>"），選項照能力宣告的 values（vms#35）。 */
const isStrSelect = (it: SettingItem): boolean => it.control === 'select' && !it.presets?.length && Object.keys(it.enum ?? {}).length === 0;

type WriteState = { state: 'pending' | 'applied' | 'adjusted' | 'failed'; err?: string };

export const settingsQueryKey = (dev: Pick<VmsDevice, 'homeId' | 'aid'>) => ['vms-settings', dev.homeId, dev.aid];

/** 設定表單的查詢（設定 / 智慧分析 / 技術分頁與詳細頁的分頁判斷共用同一份快取）。 */
export function useDeviceSettings(dev: VmsDevice | undefined) {
  return useQuery({
    queryKey: dev ? settingsQueryKey(dev) : ['vms-settings', '-'],
    queryFn: () => deviceApi.settings(dev!.apiEndpoint, dev!.homeId, dev!.aid),
    enabled: !!dev,
    retry: false,
    select: (d: DeviceSettings) => (d?.sections ? withVendorFirmware(d, dev?.firmware) : d),
  });
}

/** 矩形圖層的開關屬性（同 cluster）：偵測區域 zone_enabled、隱私遮罩 privacy_mask_enabled（ui.yaml hidden，由編輯器寫）。 */
const ENABLED_ATTR: Record<RectLayer, string> = { zones: 'zone_enabled', privacy_masks: 'privacy_mask_enabled' };
const enabledKey = (it: Pick<SettingItem, 'key'>, layer: RectLayer) => `${it.key.split('.')[0]}.${ENABLED_ATTR[layer]}`;
const isRectLayer = (e: SettingEditor | undefined): e is RectLayer => e === 'zones' || e === 'privacy_masks';
const parseLayer = (layer: RectLayer, v: unknown) => (layer === 'zones' ? parseZones(v) : parsePrivacyMasks(v));

const reportedOf = (shadow: Shadow, key: string): unknown => {
  const [cluster, attr] = key.split('.');
  return shadowAttr(shadow.state?.reported, cluster, attr);
};

/** 週排程的時區 = 設備時區；設備跟著家戶時區（ORG-02）。只在編輯器開著時才讀範圍（測試不必包 ScopeProvider）。 */
function HomeScheduleEditor({ homeId, ...p }: Omit<Parameters<typeof ScheduleEditor>[0], 'timeZone'> & { homeId: string }) {
  const tz = useHomeTz(homeId);
  return <ScheduleEditor {...p} timeZone={tz.siteTz ?? undefined} />;
}

type RectEditorState = { kind: RectLayer; it: SettingItem; enabled: boolean | undefined; declared: boolean; loadFailed: boolean };
type EditorState = RectEditorState | { kind: 'zone_grid'; it: SettingItem } | { kind: 'schedule'; it: SettingItem };

/** hide：這些設定 key 由頁面上別處管（例：攝影機錄影與儲存的「記憶卡」列已經管 sd_enabled / sd_mode，第四輪 B4-04），表單不重複畫。 */
export function SettingsForm({ dev, view = 'settings', hide }: { dev: VmsDevice; view?: SettingsView; hide?: string[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { session } = useApp();
  const user = session?.user?.user_id ?? 'anon';
  const qk = settingsQueryKey(dev);
  const q = useDeviceSettings(dev);
  const [writes, setWrites] = useState<Record<string, WriteState>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [sp, setSp] = useSearchParams();
  const [advOpen, setAdvOpen] = useState<Record<string, boolean>>({});
  const { ask, dialog } = useConfirm();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [opening, setOpening] = useState(false);

  const sections = hideRows(layoutTiers(q.data, view), hide);
  // 偵測區域 / 隱私遮罩列的摘要要看開關屬性（ui.yaml hidden，不在設定回應裡）：有這兩種列時另外讀一次 shadow
  const rectItems = allItems(q.data).filter((i) => isRectLayer(i.editor));
  const shadowKey = ['vms-shadow', dev.homeId, dev.aid];
  // 錄影畫質（quality_tier）的選項要寫這台實際的解析度（kinio_video 的主 / 次串流）、照能力宣告的 values 收窄：有這列時讀 shadow 與 thing
  const hasQuality = allItems(q.data).some((i) => i.key === QUALITY_TIER_KEY);
  // 字串型 select（沒有 enum，例 kinio_video.main_resolution）的選項 = 生效能力宣告的 values（vms#35，同 App KinioCapLimits.stringValues）
  const hasStrSelect = allItems(q.data).some(isStrSelect);
  const shadowQ = useQuery({
    queryKey: shadowKey,
    queryFn: () => getShadow(dev.apiEndpoint, dev.homeId, dev.aid),
    enabled: rectItems.length > 0 || hasQuality,
    retry: false,
  });
  const thingQ = useQuery({
    queryKey: ['vms-thing', dev.homeId, dev.aid],
    queryFn: () => getThing(dev.apiEndpoint, dev.homeId, dev.aid),
    enabled: hasQuality || hasStrSelect,
    retry: false,
  });
  const thingCaps = (thingQ.data?.thing as { capabilities?: unknown } | undefined)?.capabilities;
  const qualityValues = qualityTierValues(thingCaps);
  /** select 的選項：quality_tier 照宣告收窄、後面寫解析度（「高 · 2K（2560×1440）」）；其他照模型。 */
  const selectKeys = (it: SettingItem): string[] =>
    Object.keys(it.enum ?? {}).filter((k) => it.key !== QUALITY_TIER_KEY || !qualityValues || qualityValues.has(k) || String(it.value) === k);
  const optionText = (it: SettingItem, k: string): string => {
    const base = enumText(it, k);
    if (it.key !== QUALITY_TIER_KEY) return base;
    const r = resolutionText(qualityTierResolution(shadowQ.data?.state?.reported as Record<string, unknown> | undefined, k));
    if (!r) return base;
    return `${base} · ${r.name ? t('dset.quality_res_named', { name: r.name, dims: r.dims }) : r.dims}`;
  };
  const layerEnabledReported = (it: SettingItem) => (shadowQ.data && isRectLayer(it.editor) ? reportedOf(shadowQ.data, enabledKey(it, it.editor)) : undefined);
  /** 另一個矩形圖層（偵測區域 ↔ 隱私遮罩，兩者都宣告時編輯器可切換、另一層半透明顯示）。 */
  const otherLayerOf = (layer: RectLayer) => rectItems.find((i) => i.editor !== layer);
  const byKey = new Map(allItems(q.data).map((i) => [i.key, i]));

  /** 寫入前的確認（第 2 級）：技術項一律、碼流類、模型帶 confirm 的。 */
  const confirmWrite = (it: SettingItem, value: unknown, valueText: string, action?: string): Promise<boolean> => {
    const technical = tierOf(it) === 'technical';
    const streams = affectsStreams(it.key);
    const confirm = confirmText(it, value);
    if (!technical && !streams && !confirm) return Promise.resolve(true);
    const label = itemLabel(it);
    return ask({
      title: streams ? t('dset.streams_title') : technical ? t('dset.tech_title') : confirm!,
      body: (
        <>
          <p>{t('dset.change_body', { label, value: valueText })}</p>
          {streams && <p className="muted small">{t('dset.streams_body')}</p>}
          {technical && !streams && <p className="muted small">{t('dset.tech_body')}</p>}
          {confirm && (streams || technical) && <p>{confirm}</p>}
        </>
      ),
      action: action ?? t('dset.apply_change', { label }),
    });
  };

  const send = async (stateKey: string, state: Record<string, Record<string, unknown>>, target: ConvergeTarget, clearDraft?: string) => {
    setWrites((w) => ({ ...w, [stateKey]: { state: 'pending' } }));
    try {
      await setDesired(dev.apiEndpoint, dev.homeId, dev.aid, state, 'vms-web');
      const shadow = await converge(dev.apiEndpoint, dev.homeId, dev.aid, target, {
        repost: () => setDesired(dev.apiEndpoint, dev.homeId, dev.aid, state, 'vms-web'),
      });
      setWrites((w) => ({ ...w, [stateKey]: { state: adopted(shadow, target) ? 'applied' : 'adjusted' } }));
      if (clearDraft)
        setDraft((d) => {
          const n = { ...d };
          delete n[clearDraft];
          return n;
        });
    } catch (e) {
      const err = e as { code?: string; message?: string };
      setWrites((w) => ({ ...w, [stateKey]: { state: 'failed', err: err.code === 'CONVERGE_TIMEOUT' ? t('dset.timeout') : (err.code ?? err.message ?? String(e)) } }));
    }
    void qc.invalidateQueries({ queryKey: qk });
  };

  const write = async (it: SettingItem, value: unknown) => {
    const text = isStrSelect(it) && typeof value === 'string' ? capStringText(value) : settingText(it, value);
    if (!(await confirmWrite(it, value, text))) return;
    const [cluster, attr] = it.key.split('.');
    await send(it.key, { [cluster]: { [attr]: value } }, { cluster, attr, value }, it.key);
  };

  const writeSegment = async (name: string, members: SettingItem[], choice: string) => {
    const chosen = members.find((m) => m.key === choice);
    const opts = segmentOptions(name, members, q.data?.segments?.[name]);
    const label = opts.find((o) => o.id === choice)?.label ?? choice;
    const gate = chosen ?? members[0];
    if ((chosen?.confirm || tierOf(gate) === 'technical') && !(await confirmWrite(gate, !!chosen, label, t('dset.segment_switch', { label })))) return;
    const [cluster, attr] = (chosen ?? members[0]).key.split('.');
    await send(`segment:${name}`, segmentDesired(members, choice), { cluster, attr, value: !!chosen });
  };

  /** 編輯器的整值寫入：desired → 等設備 ack（desired 清掉）→ 回收斂時的 reported 原值（編輯器以它重畫）。 */
  const writeRaw = async (
    stateKey: string,
    state: Record<string, Record<string, unknown>>,
    target: ConvergeTarget,
    same: (reported: unknown, shadow: Shadow) => boolean,
    also?: ConvergeTarget,
  ): Promise<{ error: string; reported?: undefined; shadow?: undefined } | { error: null; reported: unknown; shadow: Shadow }> => {
    setWrites((w) => ({ ...w, [stateKey]: { state: 'pending' } }));
    try {
      await setDesired(dev.apiEndpoint, dev.homeId, dev.aid, state, 'vms-web');
      const repost = () => setDesired(dev.apiEndpoint, dev.homeId, dev.aid, state, 'vms-web');
      let shadow = await converge(dev.apiEndpoint, dev.homeId, dev.aid, target, { repost });
      if (also) shadow = await converge(dev.apiEndpoint, dev.homeId, dev.aid, also, { repost });
      const reported = reportedOf(shadow, `${target.cluster}.${target.attr}`);
      setWrites((w) => ({ ...w, [stateKey]: { state: same(reported, shadow) ? 'applied' : 'adjusted' } }));
      return { error: null, reported, shadow };
    } catch (e) {
      const err = e as { code?: string; message?: string };
      const text = err.code === 'CONVERGE_TIMEOUT' ? t('dset.timeout') : (err.code ?? err.message ?? String(e));
      setWrites((w) => ({ ...w, [stateKey]: { state: 'failed', err: text } }));
      return { error: text as string };
    } finally {
      void qc.invalidateQueries({ queryKey: qk });
    }
  };

  const openRects = async (it: SettingItem, layer: RectLayer) => {
    setOpening(true);
    // 開關屬性在 ui.yaml 是 hidden（不在設定回應裡），從 shadow 讀；沒宣告 = 只寫矩形（空陣列 = 全畫面 / 沒有遮罩）
    const sh = await getShadow(dev.apiEndpoint, dev.homeId, dev.aid).catch(() => null);
    setOpening(false);
    // 讀不到 shadow ≠ 沒宣告：不能猜寫入語意（會把保留的矩形寫成空陣列），只給唯讀檢視
    const en = sh ? reportedOf(sh, enabledKey(it, layer)) : undefined;
    setEditor({ kind: layer, it, enabled: typeof en === 'boolean' ? en : undefined, declared: typeof en === 'boolean', loadFailed: !sh });
  };

  const saveRects = (it: SettingItem, layer: RectLayer, declared: boolean) => async (v: ZoneValue) => {
    const [cluster, attr] = it.key.split('.');
    const enAttr = ENABLED_ATTR[layer];
    const encode = layer === 'zones' ? encodeZones : encodePrivacyMasks;
    const converged = layer === 'zones' ? zonesConverged : privacyMasksConverged;
    // 全畫面 / 遮罩關：有開關屬性就寫 false 並保留矩形；沒有就寫空陣列（SPEC #ls2505-334）
    const rects = encode(declared || v.enabled ? v.zones : []);
    const state: Record<string, unknown> = { [attr]: rects };
    if (declared) state[enAttr] = v.enabled;
    const sameEnabled = (en: unknown) => !declared || en === v.enabled;
    const res = await writeRaw(
      it.key,
      { [cluster]: state },
      { cluster, attr, value: rects },
      (r, sh) => converged(r, rects) && sameEnabled(reportedOf(sh, `${cluster}.${enAttr}`)),
      // 矩形沒變（只切開關）時矩形那格的 desired 也會清掉：開關也要等到設備 ack
      declared ? { cluster, attr: enAttr, value: v.enabled } : undefined,
    );
    void qc.invalidateQueries({ queryKey: shadowKey });
    if (res.error !== null) return res;
    const en = res.shadow ? reportedOf(res.shadow, `${cluster}.${enAttr}`) : undefined;
    return { error: null, reported: res.reported, reportedEnabled: typeof en === 'boolean' ? en : undefined };
  };

  const saveGrid = (it: SettingItem) => (grid: ZoneGrid) => {
    const [cluster, attr] = it.key.split('.');
    const wire = encodeZoneGrid(grid);
    return writeRaw(it.key, { [cluster]: { [attr]: wire } }, { cluster, attr, value: wire }, (r) => zoneGridConverged(r, wire));
  };

  const saveSchedule = (it: SettingItem) => (wire: string[]) => {
    const [cluster, attr] = it.key.split('.');
    return writeRaw(it.key, { [cluster]: { [attr]: wire } }, { cluster, attr, value: wire }, (r) => scheduleConverged(r, wire));
  };

  /** 編輯器列：摘要 + 〔編輯〕（不能寫也能點進去看）。 */
  const editorRow = (it: SettingItem, kind: SettingEditor, enabled: boolean): ReactNode => {
    let summary: string;
    if (kind === 'zones') {
      const n = layerEnabledReported(it) === false ? 0 : (parseZones(it.value)?.length ?? 0);
      summary = n === 0 ? t('zed.full_frame') : n === 1 ? t('zed.count_one') : t('zed.count', { count: n });
    } else if (kind === 'privacy_masks') {
      const n = layerEnabledReported(it) === false ? 0 : (parsePrivacyMasks(it.value)?.length ?? 0);
      summary = n === 0 ? t('zed.mask_off') : n === 1 ? t('zed.mask_count_one') : t('zed.mask_count', { count: n });
    } else if (kind === 'zone_grid') {
      const s = zoneGridSummary(it.value);
      summary = s ? t(`zed.grid_${s}`) : t('dset.unknown');
    } else {
      const s = scheduleSummary(it.value);
      summary = s ? t(`zed.sum_${s}`) : t('dset.unknown');
    }
    return (
      <span className="dset-num">
        <span data-testid={`zed_summary_${it.key}`}>{summary}</span>
        <button
          type="button"
          className="btn btn-sm"
          disabled={!enabled || opening}
          onClick={() => (isRectLayer(kind) ? void openRects(it, kind) : setEditor({ kind, it }))}
          data-testid={`zed_open_${it.key}`}
        >
          {it.can_write ? t('zed.edit') : t('zed.view')}
        </button>
      </span>
    );
  };

  const offline = dev.presence === 'offline';
  const rectEditor = (e: RectEditorState) => {
    const other = otherLayerOf(e.kind);
    const otherEnabled = other ? layerEnabledReported(other) : undefined;
    return (
      <ZoneEditor
        key={e.kind}
        dev={dev}
        layer={e.kind}
        title={itemLabel(e.it)}
        reportedZones={e.it.value}
        reportedEnabled={e.enabled}
        canSave={e.it.can_write && !offline && !e.loadFailed}
        readOnlyReason={e.loadFailed ? t('zed.load_failed') : offline ? t('zed.offline') : !e.it.can_write ? t('zed.no_write') : undefined}
        other={other && isRectLayer(other.editor) ? { layer: other.editor, label: itemLabel(other), rects: otherEnabled === false ? [] : (parseLayer(other.editor, other.value) ?? []) } : undefined}
        onSwitchLayer={other && isRectLayer(other.editor) ? () => void openRects(other, other.editor as RectLayer) : undefined}
        onSave={saveRects(e.it, e.kind, e.declared)}
        onClose={() => setEditor(null)}
      />
    );
  };
  const editorNode = !editor ? null : editor.kind === 'zones' || editor.kind === 'privacy_masks' ? (
    rectEditor(editor)
  ) : editor.kind === 'zone_grid' ? (
    <GridEditor
      dev={dev}
      title={itemLabel(editor.it)}
      reported={editor.it.value}
      canSave={editor.it.can_write && !offline}
      readOnlyReason={offline ? t('zed.offline') : !editor.it.can_write ? t('zed.no_write') : undefined}
      onSave={saveGrid(editor.it)}
      onClose={() => setEditor(null)}
    />
  ) : (
    <HomeScheduleEditor
      homeId={dev.homeId}
      title={itemLabel(editor.it)}
      reported={editor.it.value}
      canSave={editor.it.can_write && !offline}
      readOnlyReason={offline ? t('zed.offline') : !editor.it.can_write ? t('zed.no_write') : undefined}
      onSave={saveSchedule(editor.it)}
      onClose={() => setEditor(null)}
    />
  );

  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.error) {
    const code = (q.error as { code?: string; status?: number }).status === 503 ? 'offline' : 'error';
    return <p className="error">{t(`dset.load_${code}`)}</p>;
  }
  if (sections.length === 0) return view === 'recording' ? null : <p className="muted">{t(view === 'technical' ? 'dset.tech_none' : 'dset.none')}</p>;

  const control = (it: SettingItem, enabled: boolean) => {
    const ws = writes[it.key];
    const busy = ws?.state === 'pending' || !enabled;
    const v = it.key in draft ? draft[it.key] : it.value;
    const label = itemLabel(it);
    // kinio_storage.overwrite 的 -1 / 0 / N 是列舉語意（-1 自動覆寫），數字旁邊標出來，不讓人以為 -1 是異常
    const ow = it.key === OVERWRITE_KEY ? overwriteMode(v) : undefined;
    const owLabel = ow && <OverwriteLabel m={ow} />;
    if (it.editor) return editorRow(it, it.editor, enabled);
    if (!it.can_write || it.control === 'readout' || it.control === 'custom' || it.control === 'button')
      return owLabel ?? <span className="mono" style={{ whiteSpace: 'pre-line' }}>{it.control === 'button' || it.control === 'custom' ? t('dset.use_app') : settingText(it, it.value)}</span>;
    if (it.presets?.length) {
      const sel = presetSelected(it, v);
      return (
        <select className="input" value={sel} disabled={busy} aria-label={label} onChange={(e) => void write(it, Number(e.target.value))}>
          {sel === '' && (
            <option value="" disabled hidden>
              {settingText(it, v)}
            </option>
          )}
          {it.presets.map((p) => (
            <option key={p.value} value={String(p.value)}>
              {presetText(p)}
            </option>
          ))}
        </select>
      );
    }
    switch (it.control) {
      case 'switch':
        return <input type="checkbox" checked={v === true} disabled={busy} aria-label={label} onChange={(e) => void write(it, e.target.checked)} />;
      case 'select': {
        if (isStrSelect(it)) {
          // 沒宣告可選值 = 不能改（clusters.yaml main_resolution）：唯讀顯示目前值，不畫空下拉
          const vs = capStringValues(thingCaps, it.key);
          if (!vs) return <span className="mono">{typeof v === 'string' && v !== '' ? capStringText(v) : settingText(it, v)}</span>;
          const cur = typeof v === 'string' ? v : '';
          return (
            <select className="input" value={cur} disabled={busy} aria-label={label} onChange={(e) => e.target.value !== '' && void write(it, e.target.value)}>
              {cur === '' && <option value="">—</option>}
              {cur !== '' && !vs.includes(cur) && <option value={cur}>{capStringText(cur)}</option>}
              {vs.map((s) => (
                <option key={s} value={s}>
                  {capStringText(s)}
                </option>
              ))}
            </select>
          );
        }
        return (
          <select className="input" value={v === undefined || v === null ? '' : String(v)} disabled={busy} aria-label={label} onChange={(e) => void write(it, Number(e.target.value))}>
            {v === undefined || v === null ? <option value="">—</option> : null}
            {selectKeys(it).map((k) => (
              <option key={k} value={k}>
                {optionText(it, k)}
              </option>
            ))}
          </select>
        );
      }
      case 'slider':
      case 'stepper': {
        const n = typeof v === 'number' ? v : Number(v ?? it.min ?? 0);
        return (
          <span className="dset-num">
            {it.control === 'slider' && (
              <input type="range" min={it.min} max={it.max} value={n} disabled={busy} aria-label={label} onChange={(e) => setDraft((d) => ({ ...d, [it.key]: Number(e.target.value) }))} />
            )}
            <input
              className="input"
              type="number"
              min={it.min}
              max={it.max}
              value={n}
              disabled={busy}
              aria-label={label}
              onChange={(e) => setDraft((d) => ({ ...d, [it.key]: Number(e.target.value) }))}
            />
            {displayUnits(it.key, it.units) && <span className="muted">{displayUnits(it.key, it.units)}</span>}
            {owLabel}
            {it.key in draft && draft[it.key] !== it.value && (
              <button className="btn btn-sm" disabled={busy} onClick={() => void write(it, n)}>
                {t('common.save')}
              </button>
            )}
          </span>
        );
      }
      case 'text': {
        const s = typeof v === 'string' ? v : '';
        return (
          <span className="dset-num">
            <input className="input" value={s} disabled={busy} aria-label={label} onChange={(e) => setDraft((d) => ({ ...d, [it.key]: e.target.value }))} />
            {it.key in draft && draft[it.key] !== it.value && (
              <button className="btn btn-sm" disabled={busy} onClick={() => void write(it, s)}>
                {t('common.save')}
              </button>
            )}
          </span>
        );
      }
    }
    return null;
  };

  const status = (key: string, pending: boolean) => {
    const ws = writes[key];
    const st = ws?.state ?? (pending ? 'pending' : null);
    if (!st) return null;
    return (
      <span className={`badge dset-${st}`} title={ws?.err} data-setting-state={`${key}:${st}`}>
        {t(`dset.${st}`)}
        {ws?.err ? ` · ${ws.err}` : ''}
      </span>
    );
  };

  /** 上層沒開時的說明（不藏子項）。 */
  const dependsHint = (it: SettingItem): ReactNode => {
    const p = it.depends_on ? byKey.get(it.depends_on) : undefined;
    if (!p) return null;
    const parent = itemLabel(p);
    const text = it.depends_value?.length
      ? t('dset.depends_value', { parent, values: it.depends_value.map((x) => settingText(p, x)).join(t('common.list_sep')) })
      : t('dset.depends_on', { parent });
    return (
      <div className="muted small" data-depends={it.depends_on}>
        {text}
      </div>
    );
  };

  const itemRow = (it: SettingItem) => {
    const ok = dependsOk(it, byKey);
    return (
      <tr key={it.key} data-setting={it.key} data-tier={tierOf(it)} data-disabled={!ok || undefined}>
        <th scope="row" style={{ width: '40%' }}>
          <span className={it.destructive ? 'error' : undefined}>{itemLabel(it)}</span>
          {it.write_authz === 'owner' && <span className="badge" style={{ marginLeft: 6 }}>{t('dset.owner_only')}</span>}
          {userHelp(it.help, it.key) && <div className="muted small">{userHelp(it.help, it.key)}</div>}
          {!ok && dependsHint(it)}
        </th>
        <td>{control(it, ok)}</td>
        <td style={{ width: 160 }}>{status(it.key, it.pending)}</td>
      </tr>
    );
  };

  const segmentRow = (name: string, members: SettingItem[]) => {
    const decl = q.data?.segments?.[name];
    const opts = segmentOptions(name, members, decl);
    const cur = segmentCurrent(members);
    const key = `segment:${name}`;
    const busy = writes[key]?.state === 'pending';
    const canWrite = members.every((m) => m.can_write);
    const curHelp = cur === SEGMENT_NONE ? undefined : userHelp(members.find((m) => m.key === cur)?.help, cur);
    const title = decl ? kinioLabel(decl.label_id, decl.label) : name;
    return (
      <tr key={key} data-segment={name} data-tier={tierOf(members[0])}>
        <th scope="row" style={{ width: '40%' }}>
          {title}
        </th>
        <td>
          <div className="seg" role="radiogroup" aria-label={title}>
            {opts.map((o) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={cur === o.id}
                aria-pressed={cur === o.id}
                disabled={busy || !canWrite}
                data-segment-option={`${name}_${o.id}`}
                onClick={() => cur !== o.id && void writeSegment(name, members, o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>
          {curHelp && <div className="muted small" data-segment-help={name}>{curHelp}</div>}
        </td>
        <td style={{ width: 160 }}>{status(key, members.some((m) => m.pending))}</td>
      </tr>
    );
  };

  const rows = (list: Row[], tier: string) => (
    <table className="grid" data-tier-rows={tier}>
      <tbody>{list.map((r) => (r.kind === 'item' ? itemRow(r.item) : segmentRow(r.name, r.members)))}</tbody>
    </table>
  );

  const groups = (gs: RowGroup[], tier: string) =>
    gs.map((g) => (
      <div key={g.cluster} className="dset-cluster" data-cluster={g.cluster}>
        <h4 className="muted">{g.label}</h4>
        {rows(g.rows, tier)}
      </div>
    ));

  if (view === 'technical')
    return (
      <div className="dset" data-testid="dset-technical">
        <p className="muted small">{t('dset.tech_help')}</p>
        {sections.map((s) => (
          <section key={s.name} className="card dset-section" data-tier-section={s.name}>
            <h3>{s.label}</h3>
            {groups(s.technical, 'technical')}
          </section>
        ))}
        {dialog}
        {editorNode}
      </div>
    );

  const isOpen = (name: string) => advOpen[name] ?? readAdvOpen(user, name);
  const toggle = (name: string) => {
    const next = !isOpen(name);
    writeAdvOpen(user, name, next);
    setAdvOpen((o) => ({ ...o, [name]: next }));
  };
  const pane = (s: (typeof sections)[number]) => {
    const n = rowCount(s.advanced);
    const open = isOpen(s.name);
    return (
      <section key={s.name} className="card dset-section" data-tier-section={s.name}>
        <h3>{s.label}</h3>
        {s.common.length > 0 && rows(s.common, 'common')}
        {/* 這一區沒有常用項目：不叫人去展開，直接把進階畫出來（SPEC DEV-06「空區不畫」） */}
        {s.common.length === 0 && n > 0 && <div data-adv-body={s.name}>{groups(s.advanced, 'advanced')}</div>}
        {s.common.length > 0 && n > 0 && (
          <>
            <button type="button" className="dset-adv-toggle" aria-expanded={open} data-adv-toggle={s.name} onClick={() => toggle(s.name)}>
              <span aria-hidden>{open ? '▾' : '▸'}</span> {t('dset.advanced_n', { n })}
            </button>
            {open && <div data-adv-body={s.name}>{groups(s.advanced, 'advanced')}</div>}
          </>
        )}
      </section>
    );
  };

  if (view === 'analytics' || view === 'recording')
    return (
      <div className="dset" data-testid={`dset-${view}`}>
        {sections.map(pane)}
        {dialog}
        {editorNode}
      </div>
    );

  const want = sp.get('sec');
  const cur = sections.find((s) => s.name === want) ?? sections[0];
  // 頁首一句話依這一區有沒有可改的項目切換（第三輪 R3-P1-8：NVR 設定 › 設備整區唯讀，卻寫「改了就會套用到設備」）
  const curRows = [...cur.common, ...cur.advanced.flatMap((g) => g.rows)];
  const curWritable = curRows.some((r) => (r.kind === 'item' ? r.item.can_write : r.members.some((m) => m.can_write)));
  return (
    <>
    <p className="muted small" data-testid="dset-auto-apply" data-writable={curWritable ? 'true' : 'false'}>
      {t(curWritable ? 'dset.auto_apply' : 'dset.read_only_section')}
    </p>
    <div className="dset-tiers" data-testid="dset-settings">
      <nav className="dset-nav" aria-label={t('dset.sections')}>
        {sections.map((s) => (
          <button
            key={s.name}
            type="button"
            aria-current={s.name === cur.name ? 'true' : undefined}
            data-section-nav={s.name}
            onClick={() =>
              setSp(
                (p) => {
                  const n = new URLSearchParams(p);
                  n.set('sec', s.name);
                  return n;
                },
                { replace: true },
              )
            }
          >
            {s.label}
          </button>
        ))}
      </nav>
      <div className="dset-pane">{pane(cur)}</div>
      {dialog}
      {editorNode}
    </div>
    </>
  );
}

